import { useI18n } from 'vue-i18n'

export function isNullOrUndefined (value) {
  return value === null || value === undefined
}

export const formatDateToYYYYMMDD = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  // Format to yyyy-mm-dd
  return `${year}-${month}-${day}`
}

export const formatTimestamp = (ts, showTime) => {
  const date = new Date(ts)
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')

  if (showTime) {
    return `${year}-${month}-${day} ${hours}:${minutes}`
  }

  return `${year}-${month}-${day}`
}

// Time of day only, "HH:MM" (24h) in the browser's time zone — the time part
// of `formatTimestamp(ts, true)`, for a moment that is known to be today.
export const formatTime = (ts) => {
  const date = new Date(ts)
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

// Locale-default date display shared across admin + account pages.
// time=false → date only; fallback is shown for empty input.
export const formatLocaleDate = (value, { time = true, fallback = '-' } = {}) => {
  if (!value) return fallback
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return time ? date.toLocaleString() : date.toLocaleDateString()
}

// Short month + 24h time, locale-default (admin tool/health consoles).
export const formatLocaleDateTime = (value, fallback = '-') => {
  if (!value) return fallback
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// PI-436: the one user-facing date shape — dd-mm-yyyy, 24h, zero-padded.
// Deliberately NOT locale-driven: the product standardises on this notation for
// every user regardless of browser locale. For the ISO shapes that native
// <input type="date"/"datetime-local"> require, use formatDateOnly /
// formatDatetimeLocal from composables/date-pickers/useDateRangeBuilder instead.
export const formatDisplayDate = (value, { time = false, fallback = '-' } = {}) => {
  if (value === null || value === undefined || value === '') return fallback
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const datePart = `${day}-${month}-${year}`
  if (!time) return datePart

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${datePart} ${hours}:${minutes}`
}

export const useFormatRole = () => {
  const { t } = useI18n()

  return role => {
    return t(`role.${role}`)
  }
}

export const useFormatTenantType = () => {
  const { t } = useI18n()

  return type => {
    return t(`tenant.type.${type.replace('-', '_')}`)
  }
}
